import { useEffect, useState } from "react";

export interface ShareArticleProps {
  url: string;
  title: string;
  description?: string;
}

function buildLinks({ url, title, description }: ShareArticleProps) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  const d = encodeURIComponent(description ?? "");
  const emailBody = encodeURIComponent(
    `${title}\n\n${description ? description + "\n\n" : ""}${url}`,
  );
  return {
    email: `mailto:?subject=${t}&body=${emailBody}`,
    x: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    whatsapp: `https://api.whatsapp.com/send?text=${t}%20${u}`,
    description: d,
  };
}

function openPopup(href: string) {
  const w = 600;
  const h = 540;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  window.open(
    href,
    "share-window",
    `popup=yes,width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`,
  );
}

export function ShareArticle({ url, title, description }: ShareArticleProps) {
  const links = buildLinks({ url, title, description });
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      setCanNativeShare(true);
    }
  }, []);

  async function copyLink() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      window.prompt("Copy this link:", url);
    }
  }

  async function nativeShare() {
    try {
      await navigator.share({ title, text: description, url });
    } catch {
      // user cancelled or sharing failed silently
    }
  }

  return (
    <section
      aria-labelledby="share-heading"
      className="share-article mt-16 border-t border-b border-border py-8"
    >
      <h2
        id="share-heading"
        className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-5"
      >
        Share this article
      </h2>
      <ul className="flex flex-wrap items-center gap-2 list-none p-0 m-0">
        {canNativeShare ? (
          <li>
            <button
              type="button"
              onClick={nativeShare}
              aria-label="Open share menu"
              className="inline-flex items-center gap-2 rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted/50 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
            >
              <ShareIcon />
              <span>Share...</span>
            </button>
          </li>
        ) : null}
        <li>
          <a
            href={links.email}
            aria-label={`Email this article: ${title}`}
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted/50 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            <EmailIcon />
            <span>Email</span>
          </a>
        </li>
        <li>
          <a
            href={links.x}
            data-newtab-ignore
            onClick={(e) => {
              e.preventDefault();
              openPopup(links.x);
            }}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on X (formerly Twitter): ${title}`}
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted/50 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            <XIcon />
            <span>X</span>
          </a>
        </li>
        <li>
          <a
            href={links.facebook}
            data-newtab-ignore
            onClick={(e) => {
              e.preventDefault();
              openPopup(links.facebook);
            }}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on Facebook: ${title}`}
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted/50 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            <FacebookIcon />
            <span>Facebook</span>
          </a>
        </li>
        <li>
          <a
            href={links.linkedin}
            data-newtab-ignore
            onClick={(e) => {
              e.preventDefault();
              openPopup(links.linkedin);
            }}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on LinkedIn: ${title}`}
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted/50 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            <LinkedInIcon />
            <span>LinkedIn</span>
          </a>
        </li>
        <li>
          <a
            href={links.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on WhatsApp: ${title}`}
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted/50 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            <WhatsAppIcon />
            <span>WhatsApp</span>
          </a>
        </li>
        <li>
          <button
            type="button"
            onClick={copyLink}
            aria-label={copied ? "Link copied to clipboard" : "Copy link to clipboard"}
            aria-live="polite"
            className="inline-flex items-center gap-2 rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted/50 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            {copied ? <CheckIcon /> : <LinkIcon />}
            <span>{copied ? "Copied" : "Copy link"}</span>
          </button>
        </li>
      </ul>
    </section>
  );
}

function iconProps() {
  return {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    "aria-hidden": true as const,
    focusable: false as const,
  };
}

function ShareIcon() {
  return (
    <svg {...iconProps()} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg {...iconProps()} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg {...iconProps()} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg {...iconProps()} fill="currentColor">
      <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg {...iconProps()} fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg {...iconProps()} fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg {...iconProps()} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...iconProps()} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
